// Einheit-Detail (README §5.5, §8) – reine Funktionen.

import { istWohnend } from '../dashboard/leerstand';
import { aktuellerStand, gesamtmiete, leseStand, sortiereStaende } from '../mieter/mietstaende';
import { mieterName, type MieterDetailRoh } from './objektDetail';

/** Ausstattungsmerkmale – dieselbe Liste wie im Einheiten-Formular. */
export const AUSSTATTUNG_OPTIONEN = [
  'Einbauküche', 'Keller', 'Gäste WC', 'Wohnberechtigungsschein',
  'Aufzug', 'Garten/-Mitbenutzung', 'WG geeignet', 'Garage / Stellplatz',
  'Balkon', 'Terrasse', 'Fußbodenheizung', 'Dachboden',
  'Abstellraum', 'Waschmaschinenanschluss', 'Rollläden', 'Badewanne',
] as const;

/** objekt_einheiten.ausstattung ist Text, kommagetrennt (wie im Einheiten-Formular). */
export function ausstattungListe(text: string | null | undefined): string[] {
  return (text ?? '').split(',').map(s => s.trim()).filter(Boolean);
}

export function ausstattungUmschalten(text: string | null | undefined, merkmal: string): string {
  const liste = ausstattungListe(text);
  const neu = liste.includes(merkmal) ? liste.filter(m => m !== merkmal) : [...liste, merkmal];
  return neu.join(', ');
}

/** „1 Jahr 3 Monate“, „5 Monate“, „unter 1 Monat“. */
export function mietdauer(beginn: string | null | undefined, heute: string): string {
  if (!beginn) return '';
  const [j1, m1, t1] = beginn.slice(0, 10).split('-').map(Number);
  const [j2, m2, t2] = heute.slice(0, 10).split('-').map(Number);
  if (!j1 || !m1 || !j2 || !m2) return '';
  let monate = (j2 - j1) * 12 + (m2 - m1) - ((t2 ?? 1) < (t1 ?? 1) ? 1 : 0);
  if (monate < 1) return 'unter 1 Monat';
  const jahre = Math.floor(monate / 12);
  monate %= 12;
  const teile = [jahre ? `${jahre} ${jahre === 1 ? 'Jahr' : 'Jahre'}` : '', monate ? `${monate} ${monate === 1 ? 'Monat' : 'Monate'}` : ''];
  return teile.filter(Boolean).join(' ');
}

export type MvStatus = 'aktiv' | 'gekuendigt' | 'ehemalig';

export interface MvEintrag {
  id: string;
  name: string;
  status: MvStatus;
  von: string | null;
  bis: string | null;
  gesamt: number;
}

/** Zeitstrahl der Mietverhältnisse einer Einheit: aktuelle zuerst, dann nach Mietbeginn absteigend. */
export function mietverhaeltnisse(mieter: readonly MieterDetailRoh[], heute: string): MvEintrag[] {
  const status = (m: MieterDetailRoh): MvStatus => {
    const s = (m.status ?? 'aktiv').toLowerCase();
    if (s.startsWith('gek')) return 'gekuendigt';
    return istWohnend(m.status) ? 'aktiv' : 'ehemalig';
  };
  const rang: Record<MvStatus, number> = { aktiv: 0, gekuendigt: 1, ehemalig: 2 };
  return mieter
    .map(m => {
      const historie = Array.isArray(m.miet_historie) ? m.miet_historie.map((r, i) => leseStand(r, i)) : [];
      const stichtag = m.mietende && m.mietende.slice(0, 10) < heute ? m.mietende.slice(0, 10) : heute;
      const stand = aktuellerStand(historie, stichtag);
      const zahl = (v: unknown) => { const n = Number(String(v ?? '').replace(',', '.')); return Number.isFinite(n) ? n : 0; };
      return {
        id: m.id,
        name: mieterName(m),
        status: status(m),
        von: m.mietbeginn?.slice(0, 10) || null,
        bis: m.mietende?.slice(0, 10) || null,
        gesamt: stand ? gesamtmiete(stand) : zahl(m.kaltmiete) + zahl(m.nebenkosten_vorauszahlung) + zahl(m.heizkosten) + zahl(m.weitere_kosten),
      };
    })
    .sort((a, b) => rang[a.status] - rang[b.status] || (b.von ?? '').localeCompare(a.von ?? ''));
}

export interface MietstandZeile { ab: string; kalt: number; gesamt: number }

/** Entwicklung der Miete aus miet_historie (älteste zuerst). */
export function mietentwicklung(historieRoh: unknown): MietstandZeile[] {
  const staende = Array.isArray(historieRoh) ? historieRoh.map((r, i) => leseStand(r, i)) : [];
  return sortiereStaende(staende).filter(s => s.gueltigAb).map(s => ({ ab: s.gueltigAb, kalt: s.kaltmiete, gesamt: gesamtmiete(s) }));
}

/** Zähler einer Einheit: über die Bezeichnung (zaehler.einheit_bezeichnung) oder den Mieter. */
export function zaehlerDerEinheit<Z extends { einheit_bezeichnung: string | null; mieter_id?: string | null }>(
  zaehler: readonly Z[], bezeichnung: string, mieterIds: readonly string[],
): Z[] {
  const b = bezeichnung.trim().toLowerCase();
  return zaehler.filter(z => (b && (z.einheit_bezeichnung ?? '').trim().toLowerCase() === b) || (!!z.mieter_id && mieterIds.includes(z.mieter_id)));
}

export interface KaufKennzahlen {
  kaufpreis: number;
  nebenkosten: number;
  investition: number;
  darlehen: number;
  eigenkapital: number;
  /** Kaltmiete × 12 / Kaufpreis in %, null ohne Kaufpreis */
  bruttorendite: number | null;
  /** monatliche Rate: Darlehen × Annuität (% p. a., wie im Einheiten-Formular) / 12 */
  rateMonat: number | null;
  zinssatz: number | null;
  bank: string;
  hatDaten: boolean;
}

const n = (v: unknown): number => { const x = Number(String(v ?? '').replace(',', '.')); return Number.isFinite(x) ? x : 0; };

export function kaufKennzahlen(e: Record<string, unknown>, kaltMonat: number): KaufKennzahlen {
  const kaufpreis = n(e.kaufpreis) + n(e.kaufpreis_stellplatz) + n(e.kaufpreis_garage);
  const nebenkosten = ['nk_makler', 'nk_notar', 'nk_anwalt', 'nk_grunderwerbsteuer', 'nk_eintragung', 'nk_gutachter'].reduce((s, k) => s + n(e[k]), 0);
  const darlehen = n(e.finanz_darlehenssumme);
  const investition = kaufpreis + nebenkosten;
  const annuitaet = n(e.finanz_annuitaet);
  return {
    kaufpreis, nebenkosten, investition, darlehen,
    eigenkapital: Math.max(0, investition - darlehen),
    bruttorendite: kaufpreis > 0 && kaltMonat > 0 ? Math.round((kaltMonat * 12 / kaufpreis) * 10000) / 100 : null,
    rateMonat: annuitaet > 0 && darlehen > 0 ? Math.round((darlehen * annuitaet / 100 / 12) * 100) / 100 : null,
    zinssatz: n(e.finanz_zinssatz) || null,
    bank: String(e.finanz_bank ?? '').trim(),
    hatDaten: kaufpreis > 0 || darlehen > 0,
  };
}

/**
 * Widgets ohne Daten werden nicht angeboten (README §8): ETV nur bei WEG, Hausgeld bei WEG
 * oder hinterlegtem Eigentümer-Eintrag, Mieterhöhung nur bei vermietetem Wohnraum ohne WEG.
 */
export function einheitWidgetAngeboten(key: string, k: { weg: boolean; vermietet: boolean; hatHausgeld: boolean; wohnraum?: boolean }): boolean {
  if (key === 'etv') return k.weg;
  if (key === 'hausgeld') return k.weg || k.hatHausgeld;
  // § 558 BGB gilt nur für Wohnraum – nicht für Garagen, Stellplätze, Gewerbe
  if (key === 'mieterh') return k.vermietet && !k.weg && k.wohnraum !== false;
  return true;
}

/** Mieter gehört zur Einheit: über einheit_id, bei Altdaten ohne einheit_id über die Bezeichnung. */
export function gehoertZurEinheit(m: { einheit_id: string | null; einheit?: string | null }, einheit: { id: string; bezeichnung: string | null }): boolean {
  if (m.einheit_id) return m.einheit_id === einheit.id;
  const b = (einheit.bezeichnung ?? '').trim();
  return !!b && (m.einheit ?? '').trim() === b;
}

export type KautionStatus = 'hinterlegt' | 'teilweise' | 'offen' | 'keine';

export interface KautionInfo { soll: number; ist: number; offen: number; status: KautionStatus }

/** Kaution aus dem Mietverhältnis (mieter.kaution = Soll, kaution_ist = eingegangen). */
export function kautionInfo(m: { kaution?: unknown; kaution_ist?: unknown } | null | undefined): KautionInfo {
  const soll = n(m?.kaution);
  const ist = n(m?.kaution_ist);
  if (soll <= 0) return { soll: 0, ist, offen: 0, status: 'keine' };
  const offen = Math.max(0, Math.round((soll - ist) * 100) / 100);
  return { soll, ist, offen, status: offen === 0 ? 'hinterlegt' : ist > 0 ? 'teilweise' : 'offen' };
}

export type PortalStatus = 'laedt' | 'keinZugang' | 'aktiv' | 'gesperrt' | 'ohneEmail';

/** profiles.status des Mieter-Zugangs → Anzeige (wie im Portal-Formular: Aktiv / Inaktiv / kein Eintrag). */
export function portalStatus(email: string | null | undefined, profilStatus: string | null | undefined): PortalStatus {
  if (!(email ?? '').trim()) return 'ohneEmail';
  if (profilStatus === 'Aktiv') return 'aktiv';
  if (profilStatus === 'Inaktiv') return 'gesperrt';
  return 'keinZugang';
}

export interface MieteTeile { kalt: number; nk: number; hk: number; weitere: number; gesamt: number }

/** Heute gültige Miete aller laufenden Mietverhältnisse der Einheit (Mietstand aus miet_historie, sonst Spalten). */
export function mieteAktuell(laufende: readonly MieterDetailRoh[], heute: string): MieteTeile {
  const t = { kalt: 0, nk: 0, hk: 0, weitere: 0 };
  for (const m of laufende) {
    const historie = Array.isArray(m.miet_historie) ? m.miet_historie.map((r, i) => leseStand(r, i)) : [];
    const stand = aktuellerStand(historie, heute);
    t.kalt += stand ? stand.kaltmiete : n(m.kaltmiete);
    t.nk += stand ? stand.betriebskosten : n(m.nebenkosten_vorauszahlung);
    t.hk += stand ? stand.heizkosten : n(m.heizkosten);
    t.weitere += stand ? stand.weitereKosten : n(m.weitere_kosten);
  }
  const r = (x: number) => Math.round(x * 100) / 100;
  return { kalt: r(t.kalt), nk: r(t.nk), hk: r(t.hk), weitere: r(t.weitere), gesamt: r(t.kalt + t.nk + t.hk + t.weitere) };
}

/** Laufende Mietverhältnisse (wohnend), am längsten wohnender zuerst. */
export function laufendeMietverhaeltnisse<M extends MieterDetailRoh>(mieter: readonly M[]): M[] {
  return mieter.filter(m => istWohnend(m.status)).sort((a, b) => (a.mietbeginn ?? '9999').localeCompare(b.mietbeginn ?? '9999'));
}

/** Fläche der Einheit: Wohnfläche, sonst Nutzfläche (Gewerbe, Garage). */
export function flaecheDerEinheit(e: { wohnflaeche?: unknown; nutzflaeche?: unknown }): number {
  return n(e.wohnflaeche) || n(e.nutzflaeche);
}
